"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import { HistogramChart } from "@/components/tool-kit/charts/histogram-chart";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { quantile, standardDeviation } from "@/lib/stats/descriptive";
import {
  type BinRule,
  buildHistogram,
  type Histogram,
  MAX_BINS,
} from "@/lib/tool-kit/charts/bins";
import {
  type NumberFormat,
  usesDecimalComma,
  valueText,
} from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { parseNumberTokens } from "@/lib/tool-kit/charts/parse";
import { stepDecimals } from "@/lib/tool-kit/charts/scale";

/** English strings. A translated content file overrides these through `ui`. */
export const HISTOGRAM_MAKER_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces or new lines",
  dataHint:
    "Paste a column from a spreadsheet or type the values. If your numbers use decimal commas, separate them with semicolons.",
  exampleData:
    "41, 48, 52, 55, 57, 59, 61, 62, 64, 65, 66, 68, 69, 70, 71, 72, 73, 74, 75, 76, 78, 79, 81, 83, 84, 86, 88, 91, 94, 98",
  exampleTitle: "Distribution of Exam Scores",
  exampleXTitle: "Exam score",
  valuesDetected: "one:{count} value|other:{count} values",
  ruleLabel: "How to choose the bins",
  ruleSturges: "Sturges' rule",
  ruleScott: "Scott's rule",
  ruleFd: "Freedman–Diaconis rule",
  ruleManual: "Set the bin width myself",
  roundWidth:
    "Round the width to a convenient number (1, 2, 2.5 or 5 × a power of 10)",
  widthLabel: "Bin width",
  startLabel: "First bin starts at (optional)",
  startPlaceholder: "Auto",
  heightLabel: "Bar height",
  frequency: "Frequency",
  relativeFrequency: "Relative frequency",
  binsHeading: "Bins and frequencies",
  workingSturges:
    "Sturges' rule: k = ⌈log₂ {n}⌉ + 1 = {k} bins, so the width is {range} ÷ {k} = {raw}.",
  workingScott: "Scott's rule: h = 3.49 × s ÷ ∛n = 3.49 × {s} ÷ ∛{n} = {raw}.",
  workingFd:
    "Freedman–Diaconis rule: h = 2 × IQR ÷ ∛n = 2 × {iqr} ÷ ∛{n} = {raw}.",
  workingRounded:
    "Rounded to a convenient width of {width}, starting at {start}.",
  workingExact: "Width {width}, starting at the minimum, {start}.",
  workingManual: "Bin width {width}, starting at {start}.",
  binsCount: "one:{count} bin|other:{count} bins",
  fellBack:
    "This rule needs spread in the data (its standard deviation or IQR is 0), so Sturges' rule was used instead.",
  convention:
    "Each bin includes its lower edge and excludes its upper edge, written [a, b), except the last bin, which includes both: [a, b].",
  binColumn: "Bin",
  intervalColumn: "Interval",
  frequencyColumn: "Frequency",
  relativeColumn: "Relative frequency",
  cumulativeColumn: "Cumulative frequency",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "X-axis title",
  yAxisTitle: "Y-axis title",
  axisTitlePlaceholderX: "Value",
  axisTitlePlaceholderY: "Frequency",
  colorLabel: "Bar color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the count above each bar",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and the gridlines out of the plot, and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy frequency table",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  chartLabel: "Histogram",
  chartDescription:
    "Histogram of {values} in {bins} from {start} to {end}. The tallest bin, {peak}, holds {peakCount}.",
  enterData: "Enter at least two numbers to draw a histogram.",
  badWidth: "Enter a bin width greater than 0.",
  tooManyBins:
    "That width would make more than {max} bins. Choose a wider bin.",
  below:
    "one:{count} value is below the first bin and is not counted.|other:{count} values are below the first bin and are not counted.",
  invalidTokens:
    "one:Left out {count} entry that is not a number: {tokens}.|other:Left out {count} entries that are not numbers: {tokens}.",
} as const;

type Strings = typeof HISTOGRAM_MAKER_STRINGS;

/** "[40, 50)" — or "[40,5; 50,5)" where the decimal mark is a comma. */
function interval(
  low: string,
  high: string,
  last: boolean,
  fmt: NumberFormat
): string {
  const separator = usesDecimalComma(fmt) ? "; " : ", ";
  return `[${low}${separator}${high}${last ? "]" : ")"}`;
}

/**
 * Decimals for edge labels: enough for the width and the first edge. Edges from a
 * rule's exact width can be irrational, so those are shown to three places.
 */
function labelDecimals(histogram: Histogram, exactEdges: boolean): number {
  const needed = Math.max(
    stepDecimals(histogram.width),
    stepDecimals(Math.abs(histogram.edges[0]) % 1)
  );
  return Math.min(exactEdges ? 6 : 3, needed);
}

/** The rule's arithmetic with the reader's numbers, then how the edges were set. */
function working(
  values: number[],
  result: Histogram,
  rule: BinRule,
  roundWidth: boolean,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string[] {
  const n = values.length;
  const number = (value: number) => valueText(fmt, value, 4);
  const range = Math.max(...values) - Math.min(...values);
  const lines: string[] = [];
  const place = { width: number(result.width), start: number(result.edges[0]) };
  if (rule === "manual") {
    lines.push(t(strings.workingManual, place));
  } else if (rule === "sturges" || result.fellBack) {
    const k = result.suggestedBins ?? 1;
    lines.push(
      t(strings.workingSturges, {
        n: fmt(n),
        k: fmt(k),
        range: number(range),
        raw: number(result.ruleWidth),
      })
    );
  } else if (rule === "scott") {
    lines.push(
      t(strings.workingScott, {
        n: fmt(n),
        s: number(standardDeviation(values)),
        raw: number(result.ruleWidth),
      })
    );
  } else {
    const iqr =
      quantile(values, 0.75, "inclusive") - quantile(values, 0.25, "inclusive");
    lines.push(
      t(strings.workingFd, {
        n: fmt(n),
        iqr: number(iqr),
        raw: number(result.ruleWidth),
      })
    );
  }
  if (rule !== "manual") {
    lines.push(
      t(roundWidth ? strings.workingRounded : strings.workingExact, place)
    );
  }
  const bins = result.counts.length;
  lines.push(tPlural(strings.binsCount, locale, bins, { count: fmt(bins) }));
  return lines;
}

export default function HistogramMakerWidget() {
  const strings = useToolUi(HISTOGRAM_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [rule, setRule] = useState<BinRule>("sturges");
  const [roundWidth, setRoundWidth] = useState(true);
  const [width, setWidth] = useState("10");
  const [start, setStart] = useState("");
  const [relative, setRelative] = useState(false);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleXTitle,
    yTitle: strings.frequency,
    color: "blue",
    valueLabels: true,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const { values, invalid } = parseNumberTokens(raw);
  const result =
    values.length >= 2
      ? buildHistogram(values, {
          rule,
          roundWidth,
          width: parseNumberInput(width),
          start: parseNumberInput(start),
        })
      : "noData";
  const histogram = typeof result === "string" ? null : result;

  function changeHeight(next: string) {
    const isRelative = next === "relative";
    setRelative(isRelative);
    // Keep the axis title in step with the bars, unless the reader wrote their own.
    const from = isRelative ? strings.frequency : strings.relativeFrequency;
    if (style.yTitle === from) {
      update({
        yTitle: isRelative ? strings.relativeFrequency : strings.frequency,
      });
    }
  }

  const edgeDecimals = histogram
    ? labelDecimals(histogram, rule === "manual" || roundWidth)
    : 0;
  const edgeText = (value: number) =>
    fmt(value, {
      minimumFractionDigits: edgeDecimals,
      maximumFractionDigits: edgeDecimals,
    });
  let cumulative = 0;
  const rows = (histogram?.counts ?? []).map((count, index, all) => {
    cumulative += count;
    const edges = histogram?.edges ?? [];
    const share = count / Math.max(1, values.length - (histogram?.below ?? 0));
    return [
      fmt(index + 1),
      interval(
        edgeText(edges[index]),
        edgeText(edges[index + 1]),
        index === all.length - 1,
        fmt
      ),
      fmt(count),
      valueText(fmt, share, 3),
      fmt(cumulative),
    ];
  });
  const head = [
    strings.binColumn,
    strings.intervalColumn,
    strings.frequencyColumn,
    strings.relativeColumn,
    strings.cumulativeColumn,
  ];
  const peak = histogram
    ? histogram.counts.indexOf(Math.max(...histogram.counts))
    : 0;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="hgm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={6}
        value={raw}
      />
      <div aria-live="polite" className="space-y-2">
        {values.length >= 2 ? (
          <p className={HINT}>
            {tPlural(strings.valuesDetected, locale, values.length, {
              count: fmt(values.length),
            })}
          </p>
        ) : (
          <Notice>{strings.enterData}</Notice>
        )}
        {invalid.length > 0 ? (
          <Notice>
            {tPlural(strings.invalidTokens, locale, invalid.length, {
              count: fmt(invalid.length),
              tokens: invalid.slice(0, 5).join(" "),
            })}
          </Notice>
        ) : null}
        {result === "badWidth" ? (
          <ErrorNotice>{strings.badWidth}</ErrorNotice>
        ) : null}
        {result === "tooManyBins" ? (
          <ErrorNotice>
            {t(strings.tooManyBins, { max: fmt(MAX_BINS) })}
          </ErrorNotice>
        ) : null}
        {histogram && histogram.below > 0 ? (
          <Notice>
            {tPlural(strings.below, locale, histogram.below, {
              count: fmt(histogram.below),
            })}
          </Notice>
        ) : null}
        {histogram?.fellBack ? <Notice>{strings.fellBack}</Notice> : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="hgm-rule"
          label={strings.ruleLabel}
          onChange={setRule}
          options={[
            { value: "sturges", label: strings.ruleSturges },
            { value: "scott", label: strings.ruleScott },
            { value: "freedman-diaconis", label: strings.ruleFd },
            { value: "manual", label: strings.ruleManual },
          ]}
          value={rule}
        />
        <PillGroup
          label={strings.heightLabel}
          name="hgm-height"
          onChange={changeHeight}
          options={[
            { value: "count", label: strings.frequency },
            { value: "relative", label: strings.relativeFrequency },
          ]}
          value={relative ? "relative" : "count"}
        />
      </div>
      {rule === "manual" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="hgm-width"
            label={strings.widthLabel}
            numeric
            onChange={setWidth}
            value={width}
          />
          <TextField
            id="hgm-start"
            label={strings.startLabel}
            numeric
            onChange={setStart}
            placeholder={strings.startPlaceholder}
            value={start}
          />
        </div>
      ) : (
        <Checkbox
          checked={roundWidth}
          label={strings.roundWidth}
          onChange={setRoundWidth}
        />
      )}
      {histogram ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...rows])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "histogram")}
          labels={strings}
          note={style.note}
          table={null}
          title={style.title}
        >
          <HistogramChart
            counts={histogram.counts}
            description={t(strings.chartDescription, {
              values: tPlural(strings.valuesDetected, locale, values.length, {
                count: fmt(values.length),
              }),
              bins: tPlural(
                strings.binsCount,
                locale,
                histogram.counts.length,
                { count: fmt(histogram.counts.length) }
              ),
              start: edgeText(histogram.edges[0]),
              end: edgeText(histogram.edges.at(-1) ?? 0),
              peak: rows[peak]?.[1] ?? "",
              peakCount: tPlural(
                strings.valuesDetected,
                locale,
                histogram.counts[peak],
                { count: fmt(histogram.counts[peak]) }
              ),
            })}
            edgeDecimals={edgeDecimals}
            edges={histogram.edges}
            fmt={fmt}
            id="hgm-chart"
            label={style.title.trim() || strings.chartLabel}
            relative={relative}
            style={style}
          />
        </ChartFigure>
      ) : null}
      {histogram ? (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.binsHeading}</h2>
          {working(
            values,
            histogram,
            rule,
            roundWidth,
            strings,
            locale,
            fmt
          ).map((line) => (
            <p className="text-foreground text-sm" key={line}>
              {line}
            </p>
          ))}
          <DataTable head={head} rows={rows} />
          <p className={HINT}>{strings.convention}</p>
        </section>
      ) : null}
      <ChartStyleFields
        axisLabels={strings}
        colorLabels={strings}
        idPrefix="hgm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}
